/**
 * Financial Indicators Panel — compact table view. Click any row to open
 * a modal with full detail, editable analysis, and evidence management.
 */

import { useMemo, useState, useCallback } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Doc, Id } from "@cvx/_generated/dataModel";
import type { FinancialPeriodEntryUI } from "./types";
import { evidencePeriodLabel } from "./financialContext";
import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { Textarea } from "@/ui/primitives/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Edit3,
  ExternalLink,
  FileText,
  Loader2,
  Minus,
  Plus,
  Save,
  Trash2,
  XCircle,
} from "lucide-react";
import { cn } from "@/utils/misc";
import { usePersistedDisclosure } from "@/utils/use-persisted-disclosure";
import { ScrollArea } from "@/ui/primitives/scroll-area";
import { CollapsibleMarkdown } from "../shared-panels";
import { toast } from "sonner";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface FinancialIndicatorsPanelProps {
  jobId: Id<"jobs">;
  /**
   * Raw summarization-task output blob (untyped on the backend). Only the
   * `estimatedInsolvencyDate` field is consulted, narrowed at the point of
   * use; callers may pass `null` to stay on the live DB data path.
   */
  output: unknown;
  isRunning: boolean;
  onViewDocument: (docId: string) => void;
  isViewer?: boolean;
  /**
   * When `true`, hides the top SummaryBar (met / not-met / insufficient
   * counts + estimated insolvency date) so the panel renders as
   * table-only. Used by the Overview (Plan) page where the surrounding
   * section already provides its own header chrome.
   */
  compact?: boolean;
  /**
   * Opens the Historical Financial Analysis destination (MOR-1280) from an
   * indicator's supporting evidence, targeting the period that evidence
   * cites. Omitted where there is no such destination (Overview).
   */
  onViewSupportingFinancials?: (target: {
    indicatorKey: string;
    period?: string;
  }) => void;
  /**
   * Controls which indicator's detail is open, by `indicatorKey`, so the
   * route can restore it on return (MOR-1280). Supplying
   * `onSelectedIndicatorChange` makes the panel controlled; otherwise it
   * tracks the open indicator itself.
   */
  selectedIndicatorKey?: string;
  onSelectedIndicatorChange?: (indicatorKey: string | undefined) => void;
}

interface IndicatorUI {
  _id: Id<"financialIndicatorEntries">;
  indicatorKey: string;
  indicatorName: string;
  category: string;
  assessment: "met" | "not_met" | "insufficient_data";
  confidence: "high" | "medium" | "low";
  reasoning: string;
  earliestPeriod: string | null;
  supportingValues: Array<{
    periodLabel: string;
    metric: string;
    value?: number;
    note: string;
  }>;
  sourceDocuments: Array<{ documentId: string; fileName: string }>;
  isEdited?: boolean;
  editHistory?: Array<{
    timestamp: number;
    editedBy: string;
    field: string;
    previousValue?: unknown;
    newValue?: unknown;
  }>;
}

type EvidenceItem = IndicatorUI["supportingValues"][number];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  if (!dateStr.includes("-") || dateStr.startsWith("FY")) return dateStr;
  const p = dateStr.split("-");
  if (p.length !== 3) return dateStr;
  const m = MONTHS[parseInt(p[1], 10) - 1];
  return m ? `${parseInt(p[2], 10)} ${m} ${p[0]}` : dateStr;
}

function formatMetricName(metric: string): string {
  return metric
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
}

function formatCurrency(value: number | undefined | null): string {
  if (value == null) return "N/A";
  const abs = Math.abs(value);
  const f =
    abs >= 1_000_000
      ? `$${(abs / 1_000_000).toFixed(1)}M`
      : `$${abs.toLocaleString()}`;
  return value < 0 ? `(${f})` : f;
}

const CATEGORY_LABELS: Record<string, string> = {
  primary_test: "Primary Tests",
  financial_analysis: "Financial Analysis",
  creditors_payables: "Creditors & Payables",
  financing: "Financing",
  asset_analysis: "Asset Analysis",
};
const CATEGORY_ORDER = [
  "primary_test",
  "financial_analysis",
  "creditors_payables",
  "financing",
  "asset_analysis",
];

/**
 * `output` is the raw agent output blob (untyped on the backend); only the
 * estimated insolvency date is read from it, narrowed here.
 */
function readEstimatedInsolvencyDate(output: unknown): string | undefined {
  if (typeof output !== "object" || output === null) return undefined;
  if (!("estimatedInsolvencyDate" in output)) return undefined;
  const value = output.estimatedInsolvencyDate;
  return typeof value === "string" ? value : undefined;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Solvency indicator cards grouped by category, with a detail modal for the
 * selected indicator. Selection is controlled by the parent when
 * `onSelectedIndicatorChange` is supplied (so it survives navigation) and
 * kept locally otherwise.
 */
export function FinancialIndicatorsPanel({
  jobId,
  output,
  isRunning,
  onViewDocument,
  isViewer,
  compact = false,
  onViewSupportingFinancials,
  selectedIndicatorKey,
  onSelectedIndicatorChange,
}: FinancialIndicatorsPanelProps) {
  const [ownSelectedKey, setOwnSelectedKey] = useState<string | undefined>();
  const isControlled = onSelectedIndicatorChange !== undefined;
  const selectedKey = isControlled ? selectedIndicatorKey : ownSelectedKey;
  /** Writes the selection to whichever store (parent or local) owns it. */
  const setSelectedKey = (key: string | undefined) =>
    isControlled ? onSelectedIndicatorChange(key) : setOwnSelectedKey(key);

  const { data: dbIndicatorsData } = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.listFinancialIndicatorEntriesForJob,
      { jobId },
    ),
  );
  // The query still declares `returns: v.array(v.any())`; its handler returns
  // financialIndicatorEntries docs verbatim, typed here at the boundary.
  const dbIndicators: Doc<"financialIndicatorEntries">[] | undefined =
    dbIndicatorsData;
  const { data: periodEntriesData } = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.listFinancialPeriodEntriesForJob,
      { jobId },
    ),
  );
  // Also declared `returns: v.array(v.any())` on the backend; only
  // sourceDocuments is read here, typed via the shared UI shape.
  const periodEntries: FinancialPeriodEntryUI[] | undefined = periodEntriesData;

  const { indicators } = useMemo(() => {
    const docMap = new Map<string, { documentId: string; fileName: string }>();
    for (const e of periodEntries ?? []) {
      for (const d of e.sourceDocuments ?? []) {
        if (!docMap.has(d.documentId)) docMap.set(d.documentId, d);
      }
    }
    const allDocs = Array.from(docMap.values());
    const items = (dbIndicators ?? []).map((i): IndicatorUI => ({
      _id: i._id,
      indicatorKey: i.indicatorKey,
      indicatorName: i.indicatorName,
      category: i.category,
      assessment: i.assessment,
      confidence: i.confidence,
      reasoning: i.reasoning,
      earliestPeriod: i.earliestIndicationDate ?? null,
      supportingValues: (i.supportingEvidence ?? []).map((e) => ({
        periodLabel: e.periodLabel ?? "",
        metric: e.metric ?? "",
        value: e.value,
        note: e.note ?? "",
      })),
      sourceDocuments:
        i.sourceDocuments?.length > 0 ? i.sourceDocuments : allDocs,
      isEdited: i.isEdited,
      editHistory: i.editHistory,
    }));
    return { indicators: items, allSourceDocs: allDocs };
  }, [dbIndicators, periodEntries]);

  const selectedIndicator =
    indicators.find((i: IndicatorUI) => i.indicatorKey === selectedKey) ?? null;
  const metCount = indicators.filter(
    (i: IndicatorUI) => i.assessment === "met",
  ).length;
  const notMetCount = indicators.filter(
    (i: IndicatorUI) => i.assessment === "not_met",
  ).length;
  const insufficientCount = indicators.filter(
    (i: IndicatorUI) => i.assessment === "insufficient_data",
  ).length;

  if (indicators.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-12">
        {isRunning ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin text-emerald-500" />
            <p className="text-sm text-muted-foreground">
              Assessing indicators...
            </p>
          </>
        ) : (
          <>
            <AlertTriangle className="h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">
              No indicators assessed yet.
            </p>
          </>
        )}
      </div>
    );
  }

  const grouped = CATEGORY_ORDER.map((cat) => ({
    category: cat,
    label: CATEGORY_LABELS[cat] ?? cat,
    items: indicators.filter((i: IndicatorUI) => i.category === cat),
  })).filter((g) => g.items.length > 0);

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] transition-shadow duration-200 hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)]">
      {!compact && (
        <SummaryBar
          metCount={metCount}
          notMetCount={notMetCount}
          insufficientCount={insufficientCount}
          total={indicators.length}
          estimatedInsolvencyDate={readEstimatedInsolvencyDate(output)}
        />
      )}

      {/* Indicator table */}
      <div className={cn(!compact && "border-t border-border")}>
        <div className="grid grid-cols-[1fr_90px_80px_110px_24px] items-center gap-2 border-b border-border bg-muted/30 px-5 py-2">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Indicator
          </span>
          <span className="text-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Status
          </span>
          <span className="text-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Confidence
          </span>
          <span className="text-right text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Since
          </span>
          <span />
        </div>

        {grouped.map((group, gi) => (
          <div key={group.category}>
            <div
              className={cn(
                "bg-muted/15 px-5 py-1.5",
                gi > 0 && "border-t border-border",
              )}
            >
              <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70">
                {group.label}
              </span>
            </div>
            {group.items.map((ind: IndicatorUI) => (
              <button
                key={ind._id}
                type="button"
                onClick={() => setSelectedKey(ind.indicatorKey)}
                className="grid w-full grid-cols-[1fr_90px_80px_110px_24px] items-center gap-2 border-b border-border/50 px-5 py-2.5 text-left transition-colors last:border-b-0 hover:bg-muted/20"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <StatusIcon assessment={ind.assessment} />
                  <span className="truncate text-xs">{ind.indicatorName}</span>
                  {ind.isEdited && (
                    <Edit3 className="h-3 w-3 shrink-0 text-amber-500" />
                  )}
                </div>
                <div className="flex justify-center">
                  <Badge
                    color={
                      ind.assessment === "met"
                        ? "red"
                        : ind.assessment === "not_met"
                          ? "green"
                          : "gray"
                    }
                    variant="soft"
                    size="sm"
                  >
                    {ind.assessment === "met"
                      ? "Met"
                      : ind.assessment === "not_met"
                        ? "Not Met"
                        : "N/A"}
                  </Badge>
                </div>
                <div className="flex justify-center">
                  <Badge variant="outline" size="sm" className="capitalize">
                    {ind.confidence}
                  </Badge>
                </div>
                <span className="text-right text-[10px] tabular-nums text-muted-foreground">
                  {ind.earliestPeriod && ind.assessment === "met"
                    ? formatDate(ind.earliestPeriod)
                    : "—"}
                </span>
                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/40" />
              </button>
            ))}
          </div>
        ))}
      </div>

      {/* Detail modal */}
      <Dialog
        open={selectedIndicator !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedKey(undefined);
        }}
      >
        <DialogContent className="flex max-h-[85vh] flex-col p-0 sm:max-w-3xl">
          {selectedIndicator && (
            <IndicatorDetailModal
              indicator={selectedIndicator}
              onViewDocument={onViewDocument}
              onClose={() => setSelectedKey(undefined)}
              isViewer={isViewer}
              onViewSupportingFinancials={
                onViewSupportingFinancials
                  ? () =>
                      onViewSupportingFinancials({
                        indicatorKey: selectedIndicator.indicatorKey,
                        period: evidencePeriodLabel(
                          selectedIndicator.supportingValues,
                          periodEntries ?? [],
                        ),
                      })
                  : undefined
              }
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Indicator Detail Modal
// ---------------------------------------------------------------------------

/**
 * Full detail for one indicator: reasoning, evidence with document links,
 * a jump to the supporting financials, and in-place editing of the
 * assessment for non-viewers.
 */
function IndicatorDetailModal({
  indicator,
  onViewDocument,
  onClose,
  isViewer,
  onViewSupportingFinancials,
}: {
  indicator: IndicatorUI;
  onViewDocument: (docId: string) => void;
  onClose: () => void;
  isViewer?: boolean;
  onViewSupportingFinancials?: () => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editReasoning, setEditReasoning] = useState(indicator.reasoning);
  const [editAssessment, setEditAssessment] = useState(indicator.assessment);
  const [editConfidence, setEditConfidence] = useState(indicator.confidence);
  const [editEvidence, setEditEvidence] = useState([
    ...indicator.supportingValues,
  ]);
  const { mutateAsync: updateEntry, isPending: isSaving } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.financialAnalysis.publicMutations
        .updateFinancialIndicatorEntry,
    ),
  });
  const { mutateAsync: updateEvidence, isPending: isSavingEvidence } =
    useMutation({
      mutationFn: useConvexMutation(
        api.jobs.summarization.agents.financialAnalysis.publicMutations
          .updateIndicatorEvidence,
      ),
    });

  const handleSave = useCallback(async () => {
    try {
      await updateEntry({
        entryId: indicator._id,
        assessment:
          editAssessment !== indicator.assessment ? editAssessment : undefined,
        confidence:
          editConfidence !== indicator.confidence ? editConfidence : undefined,
        reasoning:
          editReasoning !== indicator.reasoning ? editReasoning : undefined,
      });

      const evidenceChanged =
        JSON.stringify(editEvidence) !==
        JSON.stringify(indicator.supportingValues);
      if (evidenceChanged) {
        await updateEvidence({
          entryId: indicator._id,
          supportingEvidence: editEvidence,
        });
      }

      toast.success("Indicator updated");
      setIsEditing(false);
    } catch (e) {
      toast.error("Failed to save changes");
    }
  }, [
    editReasoning,
    editAssessment,
    editConfidence,
    editEvidence,
    indicator,
    updateEntry,
    updateEvidence,
  ]);

  const addEvidence = () => {
    setEditEvidence([
      ...editEvidence,
      { periodLabel: "", metric: "", value: undefined, note: "" },
    ]);
  };

  const removeEvidence = (idx: number) => {
    setEditEvidence(editEvidence.filter((_, i) => i !== idx));
  };

  const updateEvidenceField = <K extends keyof EvidenceItem>(
    idx: number,
    field: K,
    value: EvidenceItem[K],
  ) => {
    const updated = [...editEvidence];
    updated[idx][field] = value;
    setEditEvidence(updated);
  };

  const met = indicator.assessment === "met";
  const notMet = indicator.assessment === "not_met";

  return (
    <>
      {/* Header */}
      <div className="border-b border-border px-6 pb-4 pt-6">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div
              className={cn(
                "shrink-0 rounded-lg border p-2",
                met
                  ? "border-red-200 bg-red-50 dark:border-red-900/50 dark:bg-red-950/30"
                  : notMet
                    ? "border-emerald-200 bg-emerald-50 dark:border-emerald-900/50 dark:bg-emerald-950/30"
                    : "border-border bg-muted/50",
              )}
            >
              {met ? (
                <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
              ) : notMet ? (
                <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              ) : (
                <Minus className="h-5 w-5 text-muted-foreground" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <DialogTitle className="text-base">
                {indicator.indicatorName}
              </DialogTitle>
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                <Badge
                  color={met ? "red" : notMet ? "green" : "gray"}
                  variant="soft"
                  size="sm"
                >
                  {met ? "Met" : notMet ? "Not Met" : "Insufficient Data"}
                </Badge>
                <Badge variant="outline" size="sm" className="capitalize">
                  {indicator.confidence}
                </Badge>
                {indicator.earliestPeriod && met && (
                  <span className="text-xs text-muted-foreground">
                    since {formatDate(indicator.earliestPeriod)}
                  </span>
                )}
                {indicator.isEdited && (
                  <Badge
                    variant="outline"
                    size="sm"
                    className="border-amber-300 text-amber-600"
                  >
                    <Edit3 className="mr-1 h-3 w-3" /> Edited
                  </Badge>
                )}
                <span className="text-[10px] text-muted-foreground">
                  {CATEGORY_LABELS[indicator.category]}
                </span>
              </div>
            </div>
          </div>
        </DialogHeader>
      </div>

      {/* Body */}
      {/* min-h-0 lets the flex child shrink inside the max-h dialog so the
          viewport actually scrolls instead of overflowing off-screen. */}
      <ScrollArea className="min-h-0 flex-1 px-6 py-4">
        <div className="space-y-5 pr-2">
          {/* Assessment + Confidence (editable) */}
          {isEditing && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Assessment
                </label>
                <Select
                  value={editAssessment}
                  onValueChange={(v) => {
                    // The options below are the only possible values.
                    if (
                      v === "met" ||
                      v === "not_met" ||
                      v === "insufficient_data"
                    ) {
                      setEditAssessment(v);
                    }
                  }}
                >
                  <SelectTrigger className="mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="met">Met</SelectItem>
                    <SelectItem value="not_met">Not Met</SelectItem>
                    <SelectItem value="insufficient_data">
                      Insufficient Data
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Confidence
                </label>
                <Select
                  value={editConfidence}
                  onValueChange={(v) => {
                    // The options below are the only possible values.
                    if (v === "high" || v === "medium" || v === "low") {
                      setEditConfidence(v);
                    }
                  }}
                >
                  <SelectTrigger className="mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Analysis — collapsible */}
          <CollapsibleSection label="Analysis" defaultOpen>
            {isEditing ? (
              <Textarea
                value={editReasoning}
                onChange={(e) => setEditReasoning(e.target.value)}
                className="min-h-[200px] text-sm leading-relaxed focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
            ) : (
              <CollapsibleMarkdown
                text={indicator.reasoning}
                emptyLabel="No analysis recorded."
              />
            )}
          </CollapsibleSection>

          {/* Supporting evidence — collapsible */}
          <CollapsibleSection
            label="Supporting Evidence"
            count={
              isEditing
                ? editEvidence.length
                : indicator.supportingValues.length
            }
            defaultOpen
            action={
              isEditing ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 gap-1 px-2 text-xs"
                  onClick={addEvidence}
                >
                  <Plus className="h-3 w-3" /> Add
                </Button>
              ) : undefined
            }
          >
            {isEditing ? (
              <div className="space-y-2">
                {editEvidence.map((sv, i) => (
                  <div
                    key={i}
                    className="grid grid-cols-[1fr_1fr_120px_28px] items-end gap-2"
                  >
                    <div>
                      {i === 0 && (
                        <label className="mb-1 block text-[10px] text-muted-foreground">
                          Period
                        </label>
                      )}
                      <input
                        value={sv.periodLabel}
                        onChange={(e) =>
                          updateEvidenceField(i, "periodLabel", e.target.value)
                        }
                        placeholder="e.g. FY2024"
                        className="w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-xs outline-none focus:border-primary focus:ring-1 focus:ring-primary/20"
                      />
                    </div>
                    <div>
                      {i === 0 && (
                        <label className="mb-1 block text-[10px] text-muted-foreground">
                          Metric
                        </label>
                      )}
                      <input
                        value={sv.metric}
                        onChange={(e) =>
                          updateEvidenceField(i, "metric", e.target.value)
                        }
                        placeholder="e.g. netAssets"
                        className="w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-xs outline-none focus:border-primary focus:ring-1 focus:ring-primary/20"
                      />
                    </div>
                    <div>
                      {i === 0 && (
                        <label className="mb-1 block text-[10px] text-muted-foreground">
                          Value ($)
                        </label>
                      )}
                      <input
                        value={sv.value ?? ""}
                        onChange={(e) =>
                          updateEvidenceField(
                            i,
                            "value",
                            e.target.value ? Number(e.target.value) : undefined,
                          )
                        }
                        placeholder="0"
                        type="number"
                        className="w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-right text-xs tabular-nums outline-none focus:border-primary focus:ring-1 focus:ring-primary/20"
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-7 shrink-0 p-0"
                      onClick={() => removeEvidence(i)}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-red-500" />
                    </Button>
                  </div>
                ))}
                {editEvidence.length === 0 && (
                  <p className="py-2 text-xs text-muted-foreground">
                    No evidence. Click + Add to create one.
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-1.5">
                {indicator.supportingValues.map((sv, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between rounded-md border border-border/50 bg-background px-3 py-2"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium">
                        {sv.periodLabel}
                      </span>
                      <Badge variant="outline" size="sm">
                        {formatMetricName(sv.metric)}
                      </Badge>
                    </div>
                    {sv.value != null && (
                      <span
                        className={cn(
                          "text-xs font-medium tabular-nums",
                          sv.value < 0 && "text-red-600 dark:text-red-400",
                        )}
                      >
                        {formatCurrency(sv.value)}
                      </span>
                    )}
                  </div>
                ))}
                {indicator.supportingValues.length === 0 && (
                  <p className="py-2 text-xs text-muted-foreground">
                    No supporting evidence recorded.
                  </p>
                )}
                {onViewSupportingFinancials && (
                  <Button
                    variant="link"
                    size="sm"
                    className="h-auto gap-1 px-0 text-xs"
                    onClick={onViewSupportingFinancials}
                  >
                    View supporting financials
                    <ExternalLink className="h-3 w-3" />
                  </Button>
                )}
              </div>
            )}
          </CollapsibleSection>

          {/* Source documents — collapsible */}
          {indicator.sourceDocuments.length > 0 && (
            <CollapsibleSection
              label="Source Documents"
              count={indicator.sourceDocuments.length}
            >
              <div className="space-y-1">
                {indicator.sourceDocuments.map((doc) => (
                  <button
                    key={doc.documentId}
                    type="button"
                    onClick={() => onViewDocument(doc.documentId)}
                    className="group flex w-full items-center gap-2.5 rounded-md border border-border/50 bg-background px-3 py-2 text-left transition-colors hover:bg-muted/50"
                  >
                    <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />
                    <span className="flex-1 truncate text-xs text-foreground/80">
                      {doc.fileName}
                    </span>
                    <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground/0 transition-colors group-hover:text-muted-foreground" />
                  </button>
                ))}
              </div>
            </CollapsibleSection>
          )}

          {/* Edit history — collapsible */}
          {indicator.editHistory && indicator.editHistory.length > 0 && (
            <CollapsibleSection
              label="Change History"
              count={indicator.editHistory.length}
            >
              <div className="space-y-2">
                {[...indicator.editHistory].reverse().map((edit, i) => {
                  const prev =
                    typeof edit.previousValue === "string"
                      ? edit.previousValue
                      : edit.previousValue != null
                        ? JSON.stringify(edit.previousValue)
                        : null;
                  const next =
                    typeof edit.newValue === "string"
                      ? edit.newValue
                      : edit.newValue != null
                        ? JSON.stringify(edit.newValue)
                        : null;
                  const isLongText =
                    (prev?.length ?? 0) > 60 || (next?.length ?? 0) > 60;

                  return (
                    <div
                      key={i}
                      className="overflow-hidden rounded-md border border-border/30"
                    >
                      <div className="flex flex-wrap items-center gap-2 bg-muted/20 px-3 py-2 text-xs">
                        <Badge
                          color="blue"
                          variant="soft"
                          size="sm"
                          className="capitalize"
                        >
                          {edit.editedBy}
                        </Badge>
                        <span className="text-muted-foreground">changed</span>
                        <Badge
                          variant="outline"
                          size="sm"
                          className="capitalize"
                        >
                          {formatMetricName(edit.field)}
                        </Badge>
                        <span className="ml-auto text-[10px] tabular-nums text-muted-foreground">
                          {new Date(edit.timestamp).toLocaleString()}
                        </span>
                      </div>
                      {prev != null &&
                        next != null &&
                        (isLongText ? (
                          <DiffView previous={prev} current={next} />
                        ) : (
                          <div className="flex items-center gap-2 px-3 py-2 text-xs">
                            <span className="text-red-600/70 line-through dark:text-red-400/70">
                              {prev}
                            </span>
                            <span className="text-muted-foreground">
                              &rarr;
                            </span>
                            <span className="text-emerald-700 dark:text-emerald-400">
                              {next}
                            </span>
                          </div>
                        ))}
                    </div>
                  );
                })}
              </div>
            </CollapsibleSection>
          )}
        </div>
      </ScrollArea>

      {/* Footer */}
      <div className="flex items-center justify-between border-t border-border px-6 py-3">
        <div className="text-[10px] text-muted-foreground">
          {indicator.isEdited ? "Practitioner edited" : null}
        </div>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setIsEditing(false);
                  setEditReasoning(indicator.reasoning);
                  setEditAssessment(indicator.assessment);
                  setEditConfidence(indicator.confidence);
                  setEditEvidence([...indicator.supportingValues]);
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={isSaving || isSavingEvidence}
                className="gap-1.5"
              >
                {isSaving || isSavingEvidence ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Save className="h-3.5 w-3.5" />
                )}
                Save
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={onClose}>
                Close
              </Button>
              {!isViewer && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditing(true)}
                  className="gap-1.5"
                >
                  <Edit3 className="h-3.5 w-3.5" /> Edit
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Summary Bar
// ---------------------------------------------------------------------------

function SummaryBar({
  metCount,
  notMetCount,
  insufficientCount,
  total,
  estimatedInsolvencyDate,
}: {
  metCount: number;
  notMetCount: number;
  insufficientCount: number;
  total: number;
  estimatedInsolvencyDate?: string | null;
}) {
  const signal = metCount >= 5 ? "red" : metCount >= 2 ? "amber" : "green";
  const styles = {
    red: "border-red-200 dark:border-red-900/50 bg-red-50/50 dark:bg-red-950/20",
    amber:
      "border-amber-200 dark:border-amber-900/50 bg-amber-50/50 dark:bg-amber-950/20",
    green:
      "border-emerald-200 dark:border-emerald-900/50 bg-emerald-50/50 dark:bg-emerald-950/20",
  };
  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-between gap-2 px-5 py-3",
        styles[signal],
      )}
    >
      <div className="flex items-center gap-3 text-xs">
        <span className="font-medium">{total} assessed</span>
        <span className="text-border">|</span>
        <span className="flex items-center gap-1 font-medium text-red-700 dark:text-red-400">
          <XCircle className="h-3 w-3" /> {metCount} met
        </span>
        <span className="flex items-center gap-1 text-emerald-700 dark:text-emerald-400">
          <CheckCircle2 className="h-3 w-3" /> {notMetCount} not met
        </span>
        {insufficientCount > 0 && (
          <span className="flex items-center gap-1 text-muted-foreground">
            <Minus className="h-3 w-3" /> {insufficientCount} insufficient
          </span>
        )}
      </div>
      {estimatedInsolvencyDate && (
        <span className="text-xs">
          <span className="text-muted-foreground">Earliest: </span>
          <span className="font-semibold text-red-700 dark:text-red-400">
            {formatDate(estimatedInsolvencyDate)}
          </span>
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Diff View — highlights word-level changes between two strings
// ---------------------------------------------------------------------------

function DiffView({
  previous,
  current,
}: {
  previous: string;
  current: string;
}) {
  const prevWords = previous.split(/(\s+)/);
  const currWords = current.split(/(\s+)/);

  // Simple LCS-based word diff
  const removed: Set<number> = new Set();
  const added: Set<number> = new Set();
  const currUsed: Set<number> = new Set();

  // Find matching words
  let ci = 0;
  for (let pi = 0; pi < prevWords.length; pi++) {
    let found = false;
    for (let j = ci; j < currWords.length; j++) {
      if (!currUsed.has(j) && prevWords[pi] === currWords[j]) {
        // Mark skipped current words as added
        for (let k = ci; k < j; k++) {
          if (!currUsed.has(k) && currWords[k].trim()) added.add(k);
        }
        currUsed.add(j);
        ci = j + 1;
        found = true;
        break;
      }
    }
    if (!found && prevWords[pi].trim()) removed.add(pi);
  }
  // Remaining current words are additions
  for (let j = ci; j < currWords.length; j++) {
    if (!currUsed.has(j) && currWords[j].trim()) added.add(j);
  }

  // If no differences, just show the text
  if (removed.size === 0 && added.size === 0) {
    return (
      <div className="px-3 py-2 text-xs text-muted-foreground">
        No visible changes
      </div>
    );
  }

  return (
    <div className="space-y-2 px-3 py-2 text-xs leading-relaxed">
      {removed.size > 0 && (
        <div className="rounded border border-red-200/50 bg-red-50 px-2.5 py-1.5 dark:border-red-900/30 dark:bg-red-950/20">
          <span className="mr-1.5 text-[10px] font-medium text-red-500">-</span>
          {prevWords.map((w, i) => (
            <span
              key={i}
              className={
                removed.has(i)
                  ? "rounded bg-red-200 px-0.5 text-red-700 dark:bg-red-900/40 dark:text-red-300"
                  : "text-foreground/60"
              }
            >
              {w}
            </span>
          ))}
        </div>
      )}
      {added.size > 0 && (
        <div className="rounded border border-emerald-200/50 bg-emerald-50 px-2.5 py-1.5 dark:border-emerald-900/30 dark:bg-emerald-950/20">
          <span className="mr-1.5 text-[10px] font-medium text-emerald-500">
            +
          </span>
          {currWords.map((w, i) => (
            <span
              key={i}
              className={
                added.has(i)
                  ? "rounded bg-emerald-200 px-0.5 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                  : "text-foreground/60"
              }
            >
              {w}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Collapsible Section
// ---------------------------------------------------------------------------

function CollapsibleSection({
  label,
  count,
  defaultOpen = false,
  action,
  children,
}: {
  label: string;
  /** Omitted for single-body sections (e.g. Analysis) where a count is meaningless. */
  count?: number;
  defaultOpen?: boolean;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  // Labels are stable + unique within this panel, so key off them directly:
  // the user's show/hide choice per section persists across sessions.
  const [open, setOpen] = usePersistedDisclosure(
    `financial-indicators:${label}`,
    defaultOpen,
  );
  return (
    <div className="overflow-hidden rounded-lg border border-border/50">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-2 bg-muted/20 px-3 py-2 text-left transition-colors hover:bg-muted/30"
      >
        <ChevronRight
          className={cn(
            "h-3.5 w-3.5 text-muted-foreground transition-transform",
            open && "rotate-90",
          )}
        />
        <span className="flex-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
        {count !== undefined && (
          <Badge variant="secondary" size="sm">
            {count}
          </Badge>
        )}
        {action && <span onClick={(e) => e.stopPropagation()}>{action}</span>}
      </button>
      {open && <div className="border-t border-border/50 p-3">{children}</div>}
    </div>
  );
}

function StatusIcon({ assessment }: { assessment: string }) {
  if (assessment === "met")
    return <XCircle className="h-4 w-4 shrink-0 text-red-500" />;
  if (assessment === "not_met")
    return <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />;
  return <Minus className="h-4 w-4 shrink-0 text-muted-foreground" />;
}
